import { HTMLChakraProps, HStack, VStack, Table, Text, TableCellProps, Box, Spinner, Center } from '@chakra-ui/react';
import { OnlineReservationInListDTO, Res } from 'core/api';
import { Close } from 'core/img';
import { BasicButton, SvgIcon } from 'core/ui-next';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { DateUtils } from 'core/utils';
import React from 'react';
import { z } from 'zod';
import * as Images from 'pos/Images';
import { LargeDeviceOnly, PhoneOnly } from 'pos/ui';
import { zBusinessDay } from 'core/infrastructure-next';

type ReservationContentProps = {
	loading: boolean;
	date: Date;
	reservations: OnlineReservationInListDTO[];
	onReservationSelected: (reservation: OnlineReservationInListDTO) => void;
	onDateChanged: (date: Date) => void
}


const reservationContentFilterSchema = z.object({
	date: zBusinessDay(),
	textFilter: z.string(),
	sortBy: z.object({
		label: z.string(),
		value: z.enum(['StartTime', 'Companions', 'Customer', 'Location', 'Notes']) // Asegurase que value es keyof OnlineReservationInListDTO
	}),
	sortAsc: z.boolean()
});

const RESERVATION_FILTER_KEY = 'RESERVATION_FILTER';


type ReservationContentFilter = z.infer<typeof reservationContentFilterSchema>;

function TableHeader(props: { title: string, icon?: React.FC<React.SVGProps<SVGSVGElement>>, justify?: 'left' | 'center' | 'right', minW?: number | string } & HTMLChakraProps<'th'>) {
	const { title, icon, justify, minW } = props;
	return <Table.ColumnHeader minW={minW} {...props}>
		<HStack color={{ base: '{colors.neutral.800}', _dark: 'white' }} width={'full'} justifyContent={justify}>
			{icon && <SvgIcon src={icon} w='4' h='4' />}
			<Text>{title}</Text>
		</HStack>
	</Table.ColumnHeader>;
}


function applyFilter(
	reservations: OnlineReservationInListDTO[],
	filterBy: ReservationContentFilter
): OnlineReservationInListDTO[] {

	const { textFilter, sortBy } = filterBy;

	const matchesTextFilter = (reservation: OnlineReservationInListDTO): boolean => {
		const fieldsToCheck = [
			reservation.Customer,
			reservation.Location,
			reservation.Notes,
			reservation.Companions.toString(),
			DateUtils.formatDate(reservation.StartTime, 'HH:mm'),
		];
		return fieldsToCheck.some(field => field.toLowerCase().includes(textFilter.toLowerCase()));
	};

	return reservations.filter(matchesTextFilter).sortBy(x => x[sortBy.value]);
}

type SortOption = { value: 'StartTime' | 'Companions' | 'Customer' | 'Location' | 'Notes'; label: string };

const sortOptions: SortOption[] = [
	{ value: 'StartTime', label: Res.Web.SELECT_ONLINE_RESERVATION_FILTER_BY_START_TIME_LBL() },
	{ value: 'Companions', label: Res.Web.SELECT_ONLINE_RESERVATION_FILTER_BY_COMPANIONS_LBL() },
	{ value: 'Customer', label: Res.Web.SELECT_ONLINE_RESERVATION_FILTER_BY_CUSTOMER_LBL() },
	{ value: 'Location', label: Res.Web.SELECT_ONLINE_RESERVATION_FILTER_BY_LOCATION_LBL() },
	{ value: 'Notes', label: Res.Web.SELECT_ONLINE_RESERVATION_FILTER_BY_NOTES_LBL() }
];

function LargeDeviceOnlyReservationTable(props: {
	loading: boolean,
	reservations: OnlineReservationInListDTO[],
	selectedReservation: OnlineReservationInListDTO | null,
	setSelectedReservation: (reservation: OnlineReservationInListDTO | null) => void
}) {
	const { loading, reservations, selectedReservation, setSelectedReservation } = props;

	return (
		<Table.ScrollArea h='full'>
			<Table.Root striped stickyHeader height={'full'} >
				<Table.Header lineHeight={'80%'}>
					<Table.Row fontWeight={'semibold'} fontSize={'md'}>
						<TableHeader w={'56'} title={Res.Web.SELECT_ONLINE_RESERVATION_TABLE_CUSTOMER_LBL()} icon={Images.Journal} justify='left' />
						<TableHeader w={'32'} title={Res.Web.SELECT_ONLINE_RESERVATION_TABLE_START_TIME_LBL()} icon={Images.OnlineReservation} justify='center' />
						<TableHeader w={'32'} title={Res.Web.SELECT_ONLINE_RESERVATION_TABLE_LOCATION_LBL()} icon={Images.SelectSaleLocation} justify='center' />
						<TableHeader w={'44'} title={Res.Web.SELECT_ONLINE_RESERVATION_TABLE_COMPANIONS_LBL()} icon={Images.TableCompanions} justify='right' />
						<TableHeader title={Res.Web.SELECT_ONLINE_RESERVATION_TABLE_NOTES_LBL()} icon={Images.Notes} justify='left' />
					</Table.Row>
				</Table.Header>
				{
					loading
						? <Table.Body>
							<Table.Row h='full'>
								<Table.Cell colSpan={5}>
									<Center>
										<Spinner color={{ base: '{colors.yellow.500}', _dark: '{colors.yellow.500}' }} h='12' w='12' borderWidth={'medium'} />
									</Center>
								</Table.Cell>
							</Table.Row>
						</Table.Body>
						: <Table.Body fontSize={'sm'}>{
							reservations.length > 0
								? reservations.map((reservation, index) => {

									const cellBackGround: TableCellProps['bg'] = selectedReservation === reservation ? { base: '{colors.blue.200}', _dark: '{colors.blue.500}' } : undefined;
									return <Table.Row key={index} onClick={() => setSelectedReservation(reservation)} cursor={'pointer'} h={'9'} lineHeight={'80%'}>
										<Table.Cell background={cellBackGround}>{reservation.Customer}</Table.Cell>
										<Table.Cell textAlign={'center'} background={cellBackGround}>{DateUtils.formatDate(reservation.StartTime, 'HH:mm')}</Table.Cell>
										<Table.Cell textAlign={'center'} background={cellBackGround}>{reservation.Location}</Table.Cell>
										<Table.Cell textAlign={'end'} background={cellBackGround}>{reservation.Companions}</Table.Cell>
										<Table.Cell background={cellBackGround}>{reservation.Notes}</Table.Cell>
									</Table.Row>;
								})
								: <Table.Row h={'13'} lineHeight={'80%'}>
									<Table.Cell colSpan={5} textAlign={'center'} fontSize={'md'} fontWeight={'normal'}>
										{Res.Web.RESERVATION_DETAILS_NO_RESERVATION_AVAILABLE()}
									</Table.Cell>
								</Table.Row>
						}</Table.Body>
				}
			</Table.Root>
		</Table.ScrollArea>
	);
}

function PhoneOnlyReservationTable(props: {
	loading: boolean,
	reservations: OnlineReservationInListDTO[],
	selectedReservation: OnlineReservationInListDTO | null,
	setSelectedReservation: (reservation: OnlineReservationInListDTO | null) => void
}) {
	const { loading, reservations, selectedReservation, setSelectedReservation } = props;

	return (
		<Table.ScrollArea h={'full'}>
			<Table.Root striped stickyHeader height={'full'}>
				<Table.Header height={'10'}>
					<Table.Row fontWeight={'semibold'} fontSize={'sm'}>
						<TableHeader w={'full'} fontSize={'sm'} fontWeight={'semibold'} title={Res.Web.SELECT_ONLINE_RESERVATION_TABLE_START_TIME_LBL()} justify='left' />
					</Table.Row>
				</Table.Header>
				{
					loading
						? <Table.Body>
							<Table.Row>
								<Table.Cell colSpan={5}>
									<Center>
										<Spinner color={{ base: '{colors.yellow.500}', _dark: '{colors.yellow.500}' }} h='12' w='12' borderWidth={'medium'} />
									</Center>
								</Table.Cell>
							</Table.Row>
						</Table.Body>
						: <Table.Body fontSize={'sm'}>{
							reservations.length > 0
								? reservations.map((reservation, index) => {

									const cellBackGround: TableCellProps['bg'] = selectedReservation === reservation ? { base: '{colors.blue.200}', _dark: '{colors.blue.500}' } : undefined;

									return (<React.Fragment key={index}>
										<Table.Row onClick={() => setSelectedReservation(reservation)} h={'16'}>
											<Table.Cell background={cellBackGround} display={'flex'} justifyContent={'space-between'} gap={'1'}>
												<VStack width={'50%'} alignItems={'flex-start'} height={'-webkit-fill-available'} justifyContent={'space-between'}>
													<Text fontSize={'sm'} fontWeight={'semibold'}>{reservation.Customer}</Text>
													{reservation.Notes &&
														<HStack width={'full'} align="center" gap={'1'}>
															<Box>
																<SvgIcon src={Images.Notes} w={'4'} h={'4'} />
															</Box>
															<Text lineClamp={'2'}>{reservation.Notes}</Text>
														</HStack>
													}
												</VStack>
												<VStack width={'50%'} alignItems={'flex-end'} height={'-webkit-fill-available'} justifyContent={'space-between'}>
													<Text fontSize={'sm'} fontWeight={'semibold'}>{DateUtils.formatDate(reservation.StartTime, 'HH:mm')}</Text>
													<HStack width={'full'} justifyContent={'flex-end'}>
														<HStack gap={'1'}>
															<Box>
																<SvgIcon src={Images.TableCompanions} w={'4'} h={'4'} />
															</Box>
															<Text>{reservation.Companions}</Text>
														</HStack>
														<HStack overflow={'hidden'} gap={'1'}>
															<Box>
																<SvgIcon src={Images.SelectSaleLocation} w={'4'} h={'4'} />
															</Box>
															<Text truncate>{reservation.Location}</Text>
														</HStack>
													</HStack>
												</VStack>
											</Table.Cell>
										</Table.Row>
									</React.Fragment>
									);
								})
								: <Table.Row>
									<Table.Cell colSpan={5} textAlign={'center'} fontSize={'md'} fontWeight={'normal'}>
										{Res.Web.RESERVATION_DETAILS_NO_RESERVATION_AVAILABLE()}
									</Table.Cell>
								</Table.Row>
						}</Table.Body>
				}
			</Table.Root>
		</Table.ScrollArea>
	);
}

export function ReservationContent(props: ReservationContentProps) {

	const [filteredReservations, setFilteredReservations] = React.useState<OnlineReservationInListDTO[]>([]);
	const [selectedReservation, setSelectedReservation] = React.useState<OnlineReservationInListDTO | null>(null);

	const [filter, setFilter] = React.useState<ReservationContentFilter>({ textFilter: '', sortBy: getSortByMode(), date: props.date, sortAsc: true });

	React.useEffect(() => {
		const filtered = applyFilter([...props.reservations], filter);
		const sorted = [...filtered].sort((a, b) => compareValues(a, b, filter.sortBy.value, filter.sortAsc));

		setFilteredReservations(sorted);
	}, [props.reservations, filter]);

	const compareValues = (a: any, b: any, key: string, asc: boolean) => {
		if (key === 'StartTime') {
			const aDate = new Date(a[key]);
			const bDate = new Date(b[key]);
			return asc ? aDate.getTime() - bDate.getTime() : bDate.getTime() - aDate.getTime();
		}
		if (typeof a[key] === 'number' && typeof b[key] === 'number') {
			return asc ? a[key] - b[key] : b[key] - a[key];
		}
		return asc
			? String(a[key]).localeCompare(String(b[key]))
			: String(b[key]).localeCompare(String(a[key]));
	};

	const toggleSort = () => {
		if (filteredReservations.length < 2) return;
		setFilter({ ...filter, sortAsc: !filter.sortAsc });
	};

	const onDateChanged = async (date: Date) => {
		setSelectedReservation(null);
		setFilter({ ...filter, date, sortAsc: true });
		props.onDateChanged(date);
	};

	const onTextFilterChanged = async (text: string) => {
		const newFilter = { ...filter, textFilter: text, sortAsc: true };
		setFilter(newFilter);
	};

	const onSortByChanged = async (sortBy: SortOption) => {
		const newFilter = { ...filter, sortBy, sortAsc: true };

		saveSortByMode(sortBy);
		setFilter(newFilter);
	};

	const onTextFilterCleared = async () => {
		formRef.current?.setValue('textFilter', '');
		const newFilter = { ...filter, textFilter: '', sortAsc: true };

		setFilter(newFilter);
	};

	const formRef = React.useRef<FormRef<ReservationContentFilter>>(null);
	const { Form, DateInput, Select, Input } = useControlsFor<ReservationContentFilter>();


	return <VStack paddingY={'12'} px={{ base: '12', smDown: '2', lg: '26' }} gap={'6'} width={'full'} height={'full'}>
		<Box width={'full'}>
			<Form initialValue={filter} schema={reservationContentFilterSchema} ref={formRef}>
				<LargeDeviceOnly>
					<HStack gap={'9'} width={'full'} justifyContent={'center'}>
						<Box width={{ base: '58', lgOnly: '40' }}>
							<DateInput linkTo={'date'} onWatch={onDateChanged} />
						</Box>
						<HStack width={{ base: '107', lgOnly: '78' }}>
							<SvgIcon src={Images.Filter} w='6' h='6' />
							<Input linkTo={'textFilter'} placeholder={Res.Web.SELECT_ONLINE_RESERVATION_TEXT_FILTER_PLACEHOLDER()} onWatch={onTextFilterChanged} endElement={<SvgIcon src={Close} onClick={onTextFilterCleared} w={'5'} h={'5'} cursor={'pointer'} />} />
						</HStack>
						<HStack width={{ base: '58', lgOnly: '40' }}>
							<Box>
								<SvgIcon src={filter.sortAsc ? Images.SortByAsc : Images.SortByDesc} w='6' h='6' onClick={toggleSort} cursor="pointer" />
							</Box>
							<Select linkTo={'sortBy'} options={sortOptions} renderOption={opt => opt?.label ?? ''} multiple={false} placeholder={Res.Web.SELECT_ONLINE_RESERVATION_SORT_BY_PLACEHOLDER()} valueGetter={opt => opt?.label ?? ''} onWatch={onSortByChanged} required />
						</HStack>
					</HStack>
				</LargeDeviceOnly>
				<PhoneOnly>
					<VStack gap={'4'} width={'full'}>
						<HStack width={'full'} gap={'4'}>
							<Box width={'45%'}>
								<DateInput linkTo={'date'} onWatch={onDateChanged} />
							</Box>
							<HStack width={'55%'} gap={'0'}>
								<Box height={'10'} bg={'neutral.50'} padding={'2'} borderTopWidth={'1px'} borderRightWidth={'0'} borderLeftWidth={'1px'} borderBottomWidth={'1px'} borderColor={{ base: '{colors.neutral.300}', _dark: '{colors.neutral.600}' }} borderTopLeftRadius={'md'} borderBottomLeftRadius={'md'}>
									<SvgIcon src={filter.sortAsc ? Images.SortByAsc : Images.SortByDesc} w='6' h='6' onClick={toggleSort} cursor="pointer" />
								</Box>
								<Select linkTo={'sortBy'} options={sortOptions} renderOption={opt => opt?.label ?? ''} multiple={false} placeholder={Res.Web.SELECT_ONLINE_RESERVATION_SORT_BY_PLACEHOLDER()} valueGetter={opt => opt?.label ?? ''} onWatch={onSortByChanged} variant={'borderLeftRadiusNone'} required />
							</HStack>
						</HStack>
						<HStack width={'full'}>
							<Input linkTo={'textFilter'} placeholder={Res.Web.SELECT_ONLINE_RESERVATION_TEXT_FILTER_PLACEHOLDER()} onWatch={onTextFilterChanged} endElement={<SvgIcon src={Close} onClick={onTextFilterCleared} w={'6'} h={'6'} cursor={'pointer'} />} />
						</HStack>
					</VStack>
				</PhoneOnly>
			</Form>
		</Box>

		<Box borderRadius={'md'} borderWidth={'1px'} borderColor={{ base: '{colors.neutral.100}', _dark: '{colors.neutral.700}' }} w={'full'} shadow={'md'} overflowY={'auto'} pos={'relative'}>
			<LargeDeviceOnly>
				<LargeDeviceOnlyReservationTable
					loading={props.loading}
					reservations={filteredReservations}
					selectedReservation={selectedReservation}
					setSelectedReservation={setSelectedReservation}
				/>
			</LargeDeviceOnly>
			<PhoneOnly>
				<PhoneOnlyReservationTable
					loading={props.loading}
					reservations={filteredReservations}
					selectedReservation={selectedReservation}
					setSelectedReservation={setSelectedReservation}
				/>
			</PhoneOnly>
		</Box>

		<BasicButton
			onClick={() => props.onReservationSelected(selectedReservation!)}
			disabled={!selectedReservation}
			text={Res.Web.SELECT_SALE_LOCATION_SELECT_RESERVATION_BTN()}
			type={!selectedReservation ? 'disabled' : 'primary'}
			w={{ base: '96', smDown: 'full' }}
		/>
	</VStack>;
}

function getSortByMode(): SortOption {
	const value = localStorage.getItem(RESERVATION_FILTER_KEY);
	if (!value)
		return sortOptions[0];

	return JSON.parse(value);
}

function saveSortByMode(mode: SortOption): void {
	localStorage.setItem(RESERVATION_FILTER_KEY, JSON.stringify(mode));
}
